import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { OPTIONAL_STEPS, type BoardProposal, type Check, type DocumentsProposal, type FinancesProposal, type IdentityProposal, type MembersProposal, type OnboardingStep, type OnboardingStepState, type VendorsProposal } from '@syndiqia/onboarding/contracts';
import { Link, useNavigate } from 'react-router';
import { errorMessage, personaHeader, useApi, useAuthHeaders } from '../api';
import { Alert, Field, Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { useSyndicate } from '../session';

// Onboarding ("mise en service"): the board drops what it has, step by step; the worker reads the files; the checks say
// what is complete and what is missing; a director reviews and applies each step. Design: docs/engineering/onboarding.md.

type StepView = OnboardingStepState & { checks: Check[]; canApply: boolean };
interface OnboardingView {
  tenantStatus: 'onboarding' | 'active';
  steps: StepView[];
  people: { id: string; name: string; email: string | null; ownsUnit: boolean }[];
  existing: { lots: number; parties: number; documents: number; vendors: number; fiscalYears: number };
}

const done = (s: StepView) => s.status === 'applied' || s.status === 'skipped';

function useOnboarding() {
  const s = useSyndicate();
  const api = useApi();
  return useQuery({
    queryKey: [s.tenantId, 'onboarding'],
    queryFn: () => api<OnboardingView>(`${s.api}/onboarding`),
    refetchInterval: (q) => (q.state.data?.steps.some((x) => x.status === 'reading') ? 3000 : false),
  });
}

/** Saves through the API and refreshes the onboarding view. */
function useAct() {
  const s = useSyndicate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const act = async (fn: () => Promise<OnboardingView | unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const v = await fn();
      if (v && typeof v === 'object' && 'steps' in v) queryClient.setQueryData([s.tenantId, 'onboarding'], v);
      await queryClient.invalidateQueries({ queryKey: [s.tenantId] });
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { act, busy, error };
}

export function OnboardingPage() {
  const { t } = useI18n();
  const o = t.onboarding;
  const s = useSyndicate();
  const data = useOnboarding();
  const [selected, setSelected] = useState<OnboardingStep | null>(null);
  const steps = data.data?.steps ?? [];
  const current = selected ?? steps.find((x) => !done(x))?.step ?? steps[0]?.step ?? null;
  const index = steps.findIndex((x) => x.step === current);
  const step = steps[index];
  const fromConsole = data.data?.tenantStatus === 'onboarding';
  const go = (i: number) => {
    const target = steps[i];
    if (target) setSelected(target.step);
    window.scrollTo({ top: 0 });
  };
  const next = () => go(Math.min(steps.length - 1, index + 1));
  return (
    <>
      <PageHeader title={o.title} intro={fromConsole ? o.consoleIntro(s.name) : o.intro} />
      {data.isPending && <Loading label={t.loading} />}
      {data.isError && <Alert>{errorMessage(data.error, t)}</Alert>}
      {data.data && step && (
        <>
          <ol className="onboarding-phases" aria-label={o.title}>
            {steps.map((x, i) => (
              <li key={x.step}>
                <button type="button" className={`onboarding-phase onboarding-phase-${x.status}${x.step === current ? ' onboarding-phase-current' : ''}`} onClick={() => go(i)} aria-current={x.step === current ? 'step' : undefined}>
                  <span className="onboarding-index">{x.status === 'applied' ? '✓' : x.status === 'skipped' ? '–' : i + 1}</span>
                  <span className="onboarding-phase-text">
                    <strong>{o.steps[x.step].title}</strong>
                    <span className="small muted">{o.statuses[x.status]}</span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <section className="panel onboarding-panel">
            <h2>
              {index + 1}. {o.steps[step.step].title} <span className={`badge onboarding-badge-${step.status}`}>{o.statuses[step.status]}</span>
            </h2>
            {step.step === 'identity' ? (
              <IdentityPhase step={step} onDone={next} />
            ) : step.step === 'board' ? (
              <BoardPhase step={step} people={data.data.people} />
            ) : (
              <FilePhase step={step} onDone={next} />
            )}
          </section>
          <nav className="onboarding-nav">
            <button type="button" className="button button-small" disabled={index === 0} onClick={() => go(index - 1)}>
              ← {o.previous}
            </button>
            <Link to={fromConsole ? '/' : s.base} className="link-button small">
              {fromConsole ? o.saveQuit : o.quit}
            </Link>
            <button type="button" className="button button-small" disabled={index === steps.length - 1} onClick={next}>
              {o.next} →
            </button>
          </nav>
        </>
      )}
    </>
  );
}

function IdentityPhase({ step, onDone }: { step: StepView; onDone: () => void }) {
  const { t } = useI18n();
  const o = t.onboarding;
  const i = o.identity;
  const s = useSyndicate();
  const api = useApi();
  const { act, busy, error } = useAct();
  const p = (step.proposal as IdentityProposal | null) ?? null;
  const [v, setV] = useState({
    streetAddress: p?.streetAddress ?? '',
    city: p?.city ?? '',
    postalCode: p?.postalCode ?? '',
    contactName: p?.contactName ?? '',
    contactEmail: p?.contactEmail ?? '',
    contactPhone: p?.contactPhone ?? '',
    lotsEstimate: p?.lotsEstimate ? String(p.lotsEstimate) : '',
    plan: p?.plan ?? '',
    notes: p?.notes ?? '',
  });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const orNull = (x: string) => (x.trim() ? x.trim() : null);
  const save = (e: FormEvent) => {
    e.preventDefault();
    const proposal = {
      streetAddress: v.streetAddress.trim(),
      city: v.city.trim(),
      postalCode: orNull(v.postalCode),
      contactName: orNull(v.contactName),
      contactEmail: orNull(v.contactEmail),
      contactPhone: orNull(v.contactPhone),
      lotsEstimate: v.lotsEstimate.trim() ? Number(v.lotsEstimate) : null,
      plan: v.plan || null,
      notes: orNull(v.notes),
    };
    void act(async () => {
      await api(`${s.api}/onboarding/identity/proposal`, { method: 'PUT', body: proposal });
      return api<OnboardingView>(`${s.api}/onboarding/identity/apply`, { method: 'POST', body: {} });
    }).then((ok) => ok && onDone());
  };
  return (
    <form className="form" onSubmit={save}>
      {step.appliedSummary && <Alert tone="info">{step.appliedSummary}</Alert>}
      <div className="row">
        <Field label={i.address}>
          <input value={v.streetAddress} onChange={set('streetAddress')} required />
        </Field>
        <Field label={i.city}>
          <input value={v.city} onChange={set('city')} required />
        </Field>
        <Field label={i.postal} hint={t.common.optional}>
          <input value={v.postalCode} onChange={set('postalCode')} />
        </Field>
      </div>
      <p className="small muted">{i.contact}</p>
      <div className="row">
        <Field label={i.contactName}>
          <input value={v.contactName} onChange={set('contactName')} />
        </Field>
        <Field label={i.contactEmail}>
          <input type="email" value={v.contactEmail} onChange={set('contactEmail')} />
        </Field>
        <Field label={i.contactPhone}>
          <input value={v.contactPhone} onChange={set('contactPhone')} />
        </Field>
      </div>
      <div className="row">
        <Field label={i.lots}>
          <input inputMode="numeric" value={v.lotsEstimate} onChange={set('lotsEstimate')} />
        </Field>
        <Field label={i.plan}>
          <select value={v.plan} onChange={set('plan')}>
            <option value="">{i.none}</option>
            {(['autonome', 'assistee', 'complete'] as const).map((k) => (
              <option key={k} value={k}>
                {i.plans[k]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={i.notes} hint={t.common.optional}>
        <textarea rows={3} value={v.notes} onChange={set('notes')} />
      </Field>
      {step.checks.length > 0 && <Checks checks={step.checks} />}
      {error ? <Alert>{errorMessage(error, t)}</Alert> : null}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={busy || !v.streetAddress.trim() || !v.city.trim()}>
          {o.saveContinue}
        </button>
      </div>
    </form>
  );
}

function BoardPhase({ step, people }: { step: StepView; people: OnboardingView['people'] }) {
  const { t } = useI18n();
  const o = t.onboarding;
  const b = o.board;
  const s = useSyndicate();
  const api = useApi();
  const navigate = useNavigate();
  const { act, busy, error } = useAct();
  const chosen = new Set(((step.proposal as BoardProposal | null)?.directors ?? []).map((d) => d.partyId));
  const [picked, setPicked] = useState<Set<string>>(chosen);
  const [q, setQ] = useState('');
  const [activated, setActivated] = useState(false);
  const changed = picked.size !== chosen.size || [...picked].some((id) => !chosen.has(id));
  const shown = people.filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()) || picked.has(p.id)).slice(0, 60);
  const save = () => act(() => api<OnboardingView>(`${s.api}/onboarding/board/proposal`, { method: 'PUT', body: { directors: [...picked].map((partyId) => ({ partyId })) } }));
  if (activated || step.status === 'applied')
    return (
      <>
        <Alert tone="info">{step.appliedSummary ?? b.done}</Alert>
        <div className="form-actions">
          <Link to="/" className="button button-primary">
            {o.saveQuit}
          </Link>
        </div>
      </>
    );
  return (
    <div className="form">
      <p className="small">{b.intro}</p>
      {people.length === 0 ? (
        <p className="muted">{b.noPeople}</p>
      ) : (
        <>
          <input type="search" placeholder={b.search} value={q} onChange={(e) => setQ(e.target.value)} aria-label={b.search} />
          <div className="team-roles onboarding-people">
            {shown.map((p) => (
              <label key={p.id} className="team-role">
                <input type="checkbox" checked={picked.has(p.id)} onChange={(e) => setPicked((x) => { const n = new Set(x); if (e.target.checked) n.add(p.id); else n.delete(p.id); return n; })} />
                <span>
                  {p.name}{' '}
                  <span className="muted small">
                    {p.email ?? b.noEmail}
                    {p.ownsUnit ? ` · ${b.owner}` : ''}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </>
      )}
      {step.checks.length > 0 && !changed && <Checks checks={step.checks} />}
      {error ? <Alert>{errorMessage(error, t)}</Alert> : null}
      <div className="form-actions">
        {changed && (
          <button type="button" className="button" disabled={busy} onClick={() => void save()}>
            {b.save}
          </button>
        )}
        <button
          type="button"
          className="button button-primary"
          disabled={busy || changed || !step.canApply}
          onClick={() =>
            window.confirm(b.confirm(picked.size)) &&
            void act(async () => {
              const res = await api<{ invited: number; emailFailed: string[] }>(`${s.api}/onboarding/activate`, { method: 'POST', body: {} });
              if (res.emailFailed.length) window.alert(b.emailsNotSent(res.emailFailed.join(', ')));
              return res;
            }).then(async (ok) => {
              if (ok) {
                setActivated(true);
                navigate('/');
              }
            })
          }
        >
          {b.activate}
        </button>
      </div>
    </div>
  );
}

function FilePhase({ step, onDone }: { step: StepView; onDone: () => void }) {
  const { t } = useI18n();
  const o = t.onboarding;
  const s = useSyndicate();
  const api = useApi();
  const authHeaders = useAuthHeaders();
  const { act, busy, error } = useAct();
  const [files, setFiles] = useState<File[]>([]);
  const [rules, setRules] = useState(true);
  const info = o.steps[step.step];
  const optional = OPTIONAL_STEPS.includes(step.step);
  const upload = (e: FormEvent) => {
    e.preventDefault();
    if (!files.length) return;
    void act(async () => {
      const form = new FormData();
      for (const f of files) form.append('file', f);
      const path = `${s.api}/onboarding/${step.step}/files`;
      const res = await fetch(path, { method: 'POST', body: form, headers: { ...authHeaders(), ...personaHeader(path) } });
      const body = await res.json();
      if (!res.ok) throw Object.assign(new Error(body?.message ?? `HTTP ${res.status}`), { status: res.status, body });
      setFiles([]);
      return body as OnboardingView;
    });
  };

  return (
    <div className="onboarding-body">
      {step.status === 'applied' && step.appliedSummary && <Alert tone="info">{step.appliedSummary}</Alert>}
      <div className="onboarding-look">
        <p className="small muted">{o.lookForTitle}</p>
        <ul className="small">
          {info.lookFor.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </div>
      {step.files.length > 0 && (
        <p className="small">
          <span className="muted">{o.files} : </span>
          {step.files.map((f) => f.name).join(' · ')}
        </p>
      )}
      {step.status === 'reading' && (
        <p className="onboarding-reading">
          <span className="spinner" aria-hidden="true" /> {o.readingHint}
        </p>
      )}
      {step.error && <Alert>{step.error}</Alert>}
      {step.checks.length > 0 && step.status !== 'reading' && <Checks checks={step.checks} />}
      {step.proposal && step.status === 'ready' && <Preview step={step.step} proposal={step.proposal} onSave={(p) => void act(() => api<OnboardingView>(`${s.api}/onboarding/${step.step}/proposal`, { method: 'PUT', body: p }))} busy={busy} />}
      {step.step === 'documents' && step.status === 'ready' && (step.proposal as DocumentsProposal | null)?.rules && (
        <label className="inline-field small">
          <input type="checkbox" checked={rules} onChange={(e) => setRules(e.target.checked)} /> {o.applyRules}
        </label>
      )}
      {step.status === 'ready' && (
        <div className="form-actions">
          <button type="button" className="button button-primary" disabled={busy || !step.canApply} onClick={() => void act(() => api<OnboardingView>(`${s.api}/onboarding/${step.step}/apply`, { method: 'POST', body: { rules } })).then((ok) => ok && onDone())}>
            {o.apply}
          </button>
          <span className="small muted">{step.canApply ? o.applyHint : o.applyBlocked}</span>
        </div>
      )}
      {step.status !== 'reading' && (
        <form className="onboarding-upload" onSubmit={upload}>
          <Field label={o.drop} hint={o.dropHint}>
            <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv,.txt,.md" onChange={(e) => setFiles([...(e.target.files ?? [])])} />
          </Field>
          <div className="form-actions">
            <button type="submit" className="button button-small" disabled={busy || !files.length}>
              {busy ? o.uploading : o.upload}
            </button>
            {optional && step.status !== 'applied' && step.status !== 'skipped' && (
              <button type="button" className="button button-small" disabled={busy} onClick={() => void act(() => api<OnboardingView>(`${s.api}/onboarding/${step.step}/skip`, { method: 'POST' })).then((ok) => ok && onDone())}>
                {o.skip}
              </button>
            )}
            {step.files.length > 0 && (
              <button type="button" className="link-button small" disabled={busy} onClick={() => void act(() => api<OnboardingView>(`${s.api}/onboarding/${step.step}/reset`, { method: 'POST' }))}>
                {o.reset}
              </button>
            )}
          </div>
        </form>
      )}
      {error ? <Alert>{errorMessage(error, t)}</Alert> : null}
    </div>
  );
}

function Checks({ checks }: { checks: Check[] }) {
  const { t } = useI18n();
  const o = t.onboarding;
  const icon = { ok: '✓', warning: '⚠', missing: '✗' } as const;
  return (
    <div className="onboarding-checks">
      <p className="small muted">{o.checksTitle}</p>
      <ul>
        {checks.map((c) => (
          <li key={c.code} className={`onboarding-check onboarding-check-${c.level}`}>
            <span aria-hidden="true">{icon[c.level]}</span>
            <span>
              {(o.checks[c.code] ?? (() => c.code))(c.count ?? 0)}
              {c.items?.length ? <span className="muted"> : {c.items.join(', ')}{(c.count ?? 0) > c.items.length ? ` ${o.more((c.count ?? 0) - c.items.length)}` : ''}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Preview({ step, proposal, onSave, busy }: { step: OnboardingStep; proposal: OnboardingStepState['proposal']; onSave: (p: unknown) => void; busy: boolean }) {
  const { t, money, date } = useI18n();
  const p = t.onboarding.preview;
  if (step === 'documents') {
    const d = proposal as DocumentsProposal;
    return (
      <>
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{p.document}</th>
              <th>{p.category}</th>
              <th>{p.date}</th>
              <th>{p.visibility}</th>
            </tr>
          </thead>
          <tbody>
            {d.documents.map((x) => (
              <tr key={x.fileKey}>
                <td data-label={p.document}>
                  <strong>{x.title}</strong>
                  <div className="small muted">
                    {x.fileName}
                    {x.readable ? '' : ` · ${p.unreadable}`}
                  </div>
                </td>
                <td data-label={p.category}>{t.onboarding.categories[x.category]}</td>
                <td data-label={p.date}>{x.documentDate ? date(x.documentDate) : '—'}</td>
                <td data-label={p.visibility}>{x.visibility === 'members' ? p.members : p.board}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {d.rules && (
          <p className="small">
            <strong>{p.rules} : </strong>
            {[d.rules.voteBasis && p.voteBasis[d.rules.voteBasis], d.rules.quorumPercent && p.quorum(d.rules.quorumPercent), d.rules.noticeMinDays && p.notice(d.rules.noticeMinDays), d.rules.boardQuorumPercent && p.boardQuorum(d.rules.boardQuorumPercent)].filter(Boolean).join(' · ')}
            {d.rules.source && <span className="muted"> ({d.rules.source})</span>}
          </p>
        )}
      </>
    );
  }
  if (step === 'members') return <MembersPreview proposal={proposal as MembersProposal} onSave={onSave} busy={busy} />;
  if (step === 'finances') {
    const f = proposal as FinancesProposal;
    return (
      <>
        <dl className="facts">
          <dt>{p.fiscalYear}</dt>
          <dd>{f.fiscalYear ? `${f.fiscalYear.label} (${date(f.fiscalYear.start)} → ${date(f.fiscalYear.end)})` : '—'}</dd>
          <dt>{p.balancesDate}</dt>
          <dd>{f.balancesDate ? date(f.balancesDate) : '—'}</dd>
          <dt>{p.operating}</dt>
          <dd>{f.bank.operating !== null ? money(f.bank.operating) : '—'}</dd>
          <dt>{p.reserve}</dt>
          <dd>{f.bank.reserve !== null ? money(f.bank.reserve) : '—'}</dd>
          {f.bank.investments !== null && (
            <>
              <dt>{p.investments}</dt>
              <dd>{money(f.bank.investments)}</dd>
            </>
          )}
          {f.arrears.length > 0 && (
            <>
              <dt>{p.arrears}</dt>
              <dd>{f.arrears.map((a) => `${a.lot} : ${money(a.amountCents)}`).join(' · ')}</dd>
            </>
          )}
        </dl>
        {f.budget.length > 0 && (
          <table className="table table-stack">
            <thead>
              <tr>
                <th>{p.budget}</th>
                <th>{p.account}</th>
                <th>{p.fund}</th>
                <th className="num">{p.amount}</th>
              </tr>
            </thead>
            <tbody>
              {f.budget.map((b, i) => (
                <tr key={i}>
                  <td data-label={p.budget}>{b.label}</td>
                  <td data-label={p.account}>{b.accountNumber}</td>
                  <td data-label={p.fund}>{b.fundCode}</td>
                  <td data-label={p.amount} className="num">
                    {money(b.amountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  }
  const v = proposal as VendorsProposal;
  return (
    <table className="table table-stack">
      <thead>
        <tr>
          <th>{p.vendor}</th>
          <th>{p.trade}</th>
          <th>{p.email}</th>
          <th>{p.service}</th>
        </tr>
      </thead>
      <tbody>
        {v.vendors.map((x) => (
          <tr key={x.name}>
            <td data-label={p.vendor}>
              <strong>{x.name}</strong>
              {x.phone && <div className="small muted">{x.phone}</div>}
            </td>
            <td data-label={p.trade}>{t.maintenance.trades[x.trade]}</td>
            <td data-label={p.email}>{x.email ?? '—'}</td>
            <td data-label={p.service}>{x.service ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MembersPreview({ proposal, onSave, busy }: { proposal: MembersProposal; onSave: (p: unknown) => void; busy: boolean }) {
  const { t } = useI18n();
  const p = t.onboarding.preview;
  const [b, setB] = useState({ name: proposal.building.name ?? '', streetAddress: proposal.building.streetAddress ?? '', city: proposal.building.city ?? '', postalCode: proposal.building.postalCode ?? '' });
  const shown = proposal.rows.slice(0, 40);
  const changed = b.name !== (proposal.building.name ?? '') || b.streetAddress !== (proposal.building.streetAddress ?? '') || b.city !== (proposal.building.city ?? '') || b.postalCode !== (proposal.building.postalCode ?? '');
  const orNull = (v: string) => (v.trim() ? v.trim() : null);
  return (
    <>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ ...proposal, building: { name: orNull(b.name), streetAddress: orNull(b.streetAddress), city: orNull(b.city), postalCode: orNull(b.postalCode) } });
        }}
      >
        <p className="small muted">{p.address}</p>
        <div className="row">
          <Field label={p.name}>
            <input value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} />
          </Field>
          <Field label={p.street}>
            <input value={b.streetAddress} onChange={(e) => setB({ ...b, streetAddress: e.target.value })} />
          </Field>
          <Field label={p.city}>
            <input value={b.city} onChange={(e) => setB({ ...b, city: e.target.value })} />
          </Field>
          <Field label={p.postal}>
            <input value={b.postalCode} onChange={(e) => setB({ ...b, postalCode: e.target.value })} />
          </Field>
        </div>
        {changed && (
          <div className="form-actions">
            <button type="submit" className="button button-small" disabled={busy}>
              {t.onboarding.save}
            </button>
          </div>
        )}
      </form>
      <table className="table table-stack">
        <thead>
          <tr>
            <th>{p.lot}</th>
            <th>{p.owners}</th>
            <th>{p.email}</th>
            <th>{p.phone}</th>
            <th>{p.share}</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.lot}>
              <td data-label={p.lot}>
                <strong>{r.lot}</strong>
              </td>
              <td data-label={p.owners}>{r.owners.map((o) => o.name).join(', ') || '—'}</td>
              <td data-label={p.email}>{r.owners.map((o) => o.email ?? '—').join(', ') || '—'}</td>
              <td data-label={p.phone}>{r.owners.map((o) => o.phone ?? '—').join(', ') || '—'}</td>
              <td data-label={p.share}>{r.sharePercent !== null ? `${r.sharePercent} %` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {proposal.rows.length > shown.length && <p className="small muted">{t.onboarding.more(proposal.rows.length - shown.length)}</p>}
    </>
  );
}
